import classNames from "classnames"
import React, { ReactNode, useMemo } from "react"

import { Color } from "../../../../constants/V2/color"
import Link from "../../Link"
import ArrowIcon from "../Icons/ArrowIcon"

import {
  SharedButtonClickProps,
  handleOnClick,
} from "@utils/buttonEventHandler"
import {
  getAccentColor,
  getBackgroundColorClass,
  getBackgroundHoverColorClass,
  getBorderColorClass,
  getButtonAccentColor,
  getTextColorClass,
  getTextHoverColorClass,
} from "@utils/V2/color"

type Props = {
  color: Color
  linkUrl?: string
  mobileLinkUrl?: string
  queryParams?: string
  onClick?: () => void
  icon?: ReactNode
  disabled?: boolean
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  className?: string
  style: "solid" | "bordered"
  enterOnGroupHover?: boolean
}

const CircledIconButton = ({
  color,
  linkUrl,
  mobileLinkUrl,
  queryParams,
  onClick,
  icon = <ArrowIcon animateOnHover={false} />,
  disabled = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  className,
  style = "bordered",
  enterOnGroupHover,
}: Props) => {
  const sharedButtonClickProps: SharedButtonClickProps = {
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
    onClick,
  }

  const buttonClasses = useMemo(
    () =>
      classNames(
        style === "bordered"
          ? [
              getBorderColorClass(color),
              getBackgroundHoverColorClass(color),
              "border",
            ]
          : getBackgroundColorClass(color),
        "group block w-fit rounded-full p-[6px] sm:p-[7px] md:p-8 lg:p-[12px] transition-colors duration-300",
        linkUrl
          ? { "opacity-50 pointer-events-none": disabled }
          : "disabled:opacity-50 disabled:pointer-events-none",
        {
          "md:scale-0 md:group-hover:scale-100 md:transition-transform":
            enterOnGroupHover,
        },
        className
      ),
    [color, disabled, linkUrl, className, style]
  )

  const buttonContent = (
    <div
      className={classNames(
        style === "bordered"
          ? [
              getTextColorClass(color),
              getTextHoverColorClass(getAccentColor(color)),
            ]
          : getTextColorClass(getButtonAccentColor(color)),
        {
          "md:-translate-x-10 md:transition-transform md:delay-150 md:group-hover:translate-x-0":
            enterOnGroupHover,
        },
        "group flex h-16 w-16 items-center justify-center transition-colors duration-300 sm:h-[20px] sm:w-[20px] md:h-24 md:w-24"
      )}
    >
      {icon}
    </div>
  )

  if (linkUrl && mobileLinkUrl) {
    return (
      <>
        <Link
          link={linkUrl}
          onClick={(e) =>
            handleOnClick({ e, linkUrl, ...sharedButtonClickProps })
          }
          className={classNames("hidden md:block", buttonClasses)}
          queryParams={queryParams}
        >
          {buttonContent}
        </Link>
        <Link
          link={mobileLinkUrl}
          onClick={(e) =>
            handleOnClick({
              e,
              linkUrl: mobileLinkUrl,
              ...sharedButtonClickProps,
            })
          }
          className={classNames("block md:hidden", buttonClasses)}
          queryParams={queryParams}
        >
          {buttonContent}
        </Link>
      </>
    )
  } else if (linkUrl && !mobileLinkUrl) {
    return (
      <Link
        link={linkUrl}
        onClick={(e) =>
          handleOnClick({
            e,
            linkUrl,
            ...sharedButtonClickProps,
          })
        }
        className={buttonClasses}
        queryParams={queryParams}
      >
        {buttonContent}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) =>
        handleOnClick({
          e,
          ...sharedButtonClickProps,
        })
      }
      className={buttonClasses}
      disabled={disabled}
      aria-label="Circled Button"
    >
      {buttonContent}
    </button>
  )
}

export default CircledIconButton
